<div class="plan-manager">
  <div class="page-header">
    <h2>Plan Manager</h2>
    <p>Edit plan limits and toggle features in real-time. Changes take effect immediately — no redeployment needed.</p>
  </div>

  <!-- Plan Type Tabs -->
  <div class="plan-type-tabs">
    <button class="tab-pill" [class.active]="activeTab === 'user'" (click)="activeTab = 'user'">
      <span class="material-symbols-outlined">person</span> Member Plans ({{ plans.length }})
    </button>
    <button class="tab-pill" [class.active]="activeTab === 'broker'" (click)="activeTab = 'broker'">
      <span class="material-symbols-outlined">real_estate_agent</span> Broker Plans ({{ brokerPlans.length }})
    </button>
    <button class="tab-pill" [class.active]="activeTab === 'merchant'" (click)="activeTab = 'merchant'">
      <span class="material-symbols-outlined">storefront</span> Merchant Plans ({{ merchantPlans.length }})
    </button>
  </div>

  <!-- TAB 1: User Plans -->
  <div *ngIf="activeTab === 'user'">
    <div class="plans-grid" *ngIf="plans.length > 0">
      <div class="plan-card" *ngFor="let plan of plans" [class.editing]="editingPlanId === plan._id">

        <!-- Plan Header -->
        <div class="plan-card-header" [class]="'header-' + plan.plan_code?.toLowerCase()">
          <div class="plan-title-row">
            <div>
              <div class="plan-code">{{ plan.plan_code }}</div>
              <div class="plan-display-name">{{ plan.display_name || plan.plan_code }}</div>
            </div>
            <button class="btn-edit" (click)="startEdit(plan)" *ngIf="editingPlanId !== plan._id">
              <span class="material-symbols-outlined">edit</span>
            </button>
          </div>
        </div>

        <!-- View Mode -->
        <div class="plan-card-body" *ngIf="editingPlanId !== plan._id">
          <div class="feature-row" *ngFor="let feat of getFeatureDisplay(plan)">
            <span class="feat-label">{{ feat.label }}</span>
            <span class="feat-value" [class.feat-on]="feat.on" [class.feat-off]="feat.off">
              <span class="material-symbols-outlined" *ngIf="feat.on">check_circle</span>
              <span class="material-symbols-outlined" *ngIf="feat.off">cancel</span>
              {{ feat.value }}
            </span>
          </div>
        </div>

        <!-- Edit Mode -->
        <div class="plan-edit-body" *ngIf="editingPlanId === plan._id">
          <div class="edit-section-title">Limits</div>

          <div class="edit-row">
            <label>Daily Profile Limit</label>
            <input type="number" [(ngModel)]="editDraft.dailyProfileLimit" min="0">
          </div>
          <div class="edit-row">
            <label>Monthly Interest Limit</label>
            <input type="number" [(ngModel)]="editDraft.monthlyInterestLimit" min="0">
          </div>
          <div class="edit-row">
            <label>Boost Limit (per month)</label>
            <input type="number" [(ngModel)]="editDraft.boostMonthlyLimit" min="0">
          </div>
          <div class="edit-row">
            <label>Boost Cooldown (days)</label>
            <input type="number" [(ngModel)]="editDraft.boostCooldownDays" min="0">
          </div>
          <div class="edit-row">
            <label>Saved Filters</label>
            <input type="number" [(ngModel)]="editDraft.savedFilters" min="0">
          </div>
          <div class="edit-row">
            <label>Favorites Limit</label>
            <input type="number" [(ngModel)]="editDraft.favoritesLimit" min="0">
          </div>

          <div class="edit-section-title" style="margin-top:16px">Features</div>
          <div class="toggle-row" *ngFor="let toggle of featureToggles">
            <span class="toggle-label">{{ toggle.label }}</span>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="editDraft[toggle.key]">
              <span class="slider"></span>
            </label>
          </div>

          <div class="edit-actions">
            <button class="btn-save" (click)="savePlan(plan._id)" [disabled]="saving">
              <span class="material-symbols-outlined spin" *ngIf="saving">autorenew</span>
              {{ saving ? 'Saving...' : 'Save Changes' }}
            </button>
            <button class="btn-cancel" (click)="cancelEdit()">Cancel</button>
          </div>

          <div class="save-success" *ngIf="savedPlanId === plan._id">
            <span class="material-symbols-outlined">check_circle</span> Saved & live!
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- TAB 2: Broker Plans -->
  <div *ngIf="activeTab === 'broker'">
    <div class="plans-grid" *ngIf="brokerPlans.length > 0">
      <div class="plan-card" *ngFor="let plan of brokerPlans" [class.editing]="editingBrokerPlanId === plan._id">
        <!-- Header -->
        <div class="plan-card-header" style="background: linear-gradient(135deg, #1e293b 0%, #334155 100%);">
          <div class="plan-title-row">
            <div>
              <div class="plan-code">{{ plan.plan_code }}</div>
              <div class="plan-display-name">{{ plan.display_name }} &bull; ₹{{ plan.price }}</div>
            </div>
            <button class="btn-edit" (click)="startEditBroker(plan)" *ngIf="editingBrokerPlanId !== plan._id">
              <span class="material-symbols-outlined">edit</span>
            </button>
          </div>
        </div>

        <!-- View Mode -->
        <div class="plan-card-body" *ngIf="editingBrokerPlanId !== plan._id">
          <div class="feature-row">
            <span class="feat-label">Monthly Price</span>
            <span class="feat-value">₹{{ plan.price }}</span>
          </div>
          <div class="feature-row">
            <span class="feat-label">Max Candidates Allowed</span>
            <span class="feat-value">{{ plan.features?.max_profiles || 5 }}</span>
          </div>
          <div class="feature-row">
            <span class="feat-label">Lead Contact Access</span>
            <span class="feat-value" [class.feat-on]="plan.features?.lead_contact_access" [class.feat-off]="!plan.features?.lead_contact_access">
              <span class="material-symbols-outlined" *ngIf="plan.features?.lead_contact_access">check_circle</span>
              <span class="material-symbols-outlined" *ngIf="!plan.features?.lead_contact_access">cancel</span>
              {{ plan.features?.lead_contact_access ? 'Unlocked' : 'Locked' }}
            </span>
          </div>
          <div class="feature-row">
            <span class="feat-label">Who Viewed & Contacts</span>
            <span class="feat-value" [class.feat-on]="plan.features?.who_viewed_candidate" [class.feat-off]="!plan.features?.who_viewed_candidate">
              <span class="material-symbols-outlined" *ngIf="plan.features?.who_viewed_candidate">check_circle</span>
              <span class="material-symbols-outlined" *ngIf="!plan.features?.who_viewed_candidate">cancel</span>
              {{ plan.features?.who_viewed_candidate ? 'Pro VIP Access' : 'Locked' }}
            </span>
          </div>
          <div class="feature-row">
            <span class="feat-label">Featured Listings</span>
            <span class="feat-value" [class.feat-on]="plan.features?.featured_listings" [class.feat-off]="!plan.features?.featured_listings">
              <span class="material-symbols-outlined" *ngIf="plan.features?.featured_listings">check_circle</span>
              <span class="material-symbols-outlined" *ngIf="!plan.features?.featured_listings">cancel</span>
              {{ plan.features?.featured_listings ? 'Enabled' : 'No' }}
            </span>
          </div>
          <div class="feature-row">
            <span class="feat-label">Agency Analytics</span>
            <span class="feat-value" [class.feat-on]="plan.features?.analytics" [class.feat-off]="!plan.features?.analytics">
              <span class="material-symbols-outlined" *ngIf="plan.features?.analytics">check_circle</span>
              <span class="material-symbols-outlined" *ngIf="!plan.features?.analytics">cancel</span>
              {{ plan.features?.analytics ? 'Enabled' : 'No' }}
            </span>
          </div>
          <div class="feature-row">
            <span class="feat-label">Priority VIP Support</span>
            <span class="feat-value" [class.feat-on]="plan.features?.priority_support" [class.feat-off]="!plan.features?.priority_support">
              <span class="material-symbols-outlined" *ngIf="plan.features?.priority_support">check_circle</span>
              <span class="material-symbols-outlined" *ngIf="!plan.features?.priority_support">cancel</span>
              {{ plan.features?.priority_support ? 'Yes' : 'No' }}
            </span>
          </div>
        </div>

        <!-- Edit Mode -->
        <div class="plan-edit-body" *ngIf="editingBrokerPlanId === plan._id">
          <div class="edit-section-title">Pricing & Quota</div>
          <div class="edit-row">
            <label>Price (₹)</label>
            <input type="number" [(ngModel)]="editBrokerDraft.price" min="0">
          </div>
          <div class="edit-row">
            <label>Max Candidates</label>
            <input type="number" [(ngModel)]="editBrokerDraft.max_profiles" min="1">
          </div>

          <div class="edit-section-title" style="margin-top:16px">Broker Features</div>
          <div class="toggle-row">
            <span class="toggle-label">Lead Contact Access</span>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="editBrokerDraft.lead_contact_access">
              <span class="slider"></span>
            </label>
          </div>
          <div class="toggle-row">
            <span class="toggle-label">Who Viewed & Contacts</span>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="editBrokerDraft.who_viewed_candidate">
              <span class="slider"></span>
            </label>
          </div>
          <div class="toggle-row">
            <span class="toggle-label">Featured Listings</span>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="editBrokerDraft.featured_listings">
              <span class="slider"></span>
            </label>
          </div>
          <div class="toggle-row">
            <span class="toggle-label">Agency Analytics</span>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="editBrokerDraft.analytics">
              <span class="slider"></span>
            </label>
          </div>
          <div class="toggle-row">
            <span class="toggle-label">Priority Support</span>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="editBrokerDraft.priority_support">
              <span class="slider"></span>
            </label>
          </div>

          <div class="edit-actions">
            <button class="btn-save" (click)="saveBrokerPlan(plan._id)" [disabled]="savingBroker">
              <span class="material-symbols-outlined spin" *ngIf="savingBroker">autorenew</span>
              {{ savingBroker ? 'Saving...' : 'Save Changes' }}
            </button>
            <button class="btn-cancel" (click)="cancelEditBroker()">Cancel</button>
          </div>

          <div class="save-success" *ngIf="savedBrokerPlanId === plan._id">
            <span class="material-symbols-outlined">check_circle</span> Saved & live!
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="loading-state" *ngIf="plans.length === 0 && brokerPlans.length === 0 && merchantPlans.length === 0">
    <span class="material-symbols-outlined spin">autorenew</span> Loading plans...
  </div>
  
  <!-- TAB 3: Merchant Plans -->
  <div *ngIf="activeTab === 'merchant'">
    <div class="plans-grid" *ngIf="merchantPlans.length > 0">
      <div class="plan-card" *ngFor="let plan of merchantPlans" [class.editing]="editingMerchantPlanId === plan._id">

        <!-- Plan Header -->
        <div class="plan-card-header" [class]="'header-' + plan.planCode?.toLowerCase()">
          <div class="plan-title-row">
            <div>
              <div class="plan-code">{{ plan.planCode }}</div>
              <div class="plan-display-name">{{ plan.displayName || plan.planCode }}</div>
              <div class="plan-price" style="font-weight: 500; font-size: 14px; margin-top: 4px;">₹{{ plan.price }}/mo</div>
            </div>
            <button class="btn-edit" (click)="startMerchantEdit(plan)" *ngIf="editingMerchantPlanId !== plan._id">
              <span class="material-symbols-outlined">edit</span>
            </button>
          </div>
        </div>

        <!-- View Mode -->
        <div class="plan-card-body" *ngIf="editingMerchantPlanId !== plan._id">
          <div class="feature-row" *ngFor="let feat of getMerchantFeatureDisplay(plan)">
            <span class="feat-label">{{ feat.label }}</span>
            <span class="feat-value" [class.feat-on]="feat.on" [class.feat-off]="feat.off">
              <span class="material-symbols-outlined" *ngIf="feat.on">check_circle</span>
              <span class="material-symbols-outlined" *ngIf="feat.off">cancel</span>
              {{ feat.value }}
            </span>
          </div>
        </div>

        <!-- Edit Mode -->
        <div class="plan-edit-body" *ngIf="editingMerchantPlanId === plan._id">
          <div class="edit-section-title">Limits & Pricing</div>

          <div class="edit-row">
            <label>Monthly Price (₹)</label>
            <input type="number" [(ngModel)]="editMerchantDraft.price" min="0">
          </div>
          <div class="edit-row">
            <label>Max Services (999=Unlimited)</label>
            <input type="number" [(ngModel)]="editMerchantDraft.maxServices" min="1">
          </div>
          <div class="edit-row">
            <label>Max Images/Service (999=Unlimited)</label>
            <input type="number" [(ngModel)]="editMerchantDraft.maxImagesPerService" min="1">
          </div>

          <div class="edit-section-title" style="margin-top:16px">Features</div>
          
          <div class="toggle-row">
            <span class="toggle-label">Video Reels</span>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="editMerchantDraft.allowsVideoReels">
              <span class="slider"></span>
            </label>
          </div>
          
          <div class="toggle-row">
            <span class="toggle-label">Sponsored Ads</span>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="editMerchantDraft.allowsAds">
              <span class="slider"></span>
            </label>
          </div>

          <div class="toggle-row">
            <span class="toggle-label">Premium Badge</span>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="editMerchantDraft.hasPremiumBadge">
              <span class="slider"></span>
            </label>
          </div>

          <div class="toggle-row">
            <span class="toggle-label">Direct Phone Leads</span>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="editMerchantDraft.directPhoneLeads">
              <span class="slider"></span>
            </label>
          </div>

          <div class="toggle-row">
            <span class="toggle-label">Account Manager</span>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="editMerchantDraft.dedicatedAccountManager">
              <span class="slider"></span>
            </label>
          </div>

          <div class="edit-actions">
            <button class="btn-save" (click)="saveMerchantPlan(plan._id)" [disabled]="savingMerchant">
              <span class="material-symbols-outlined spin" *ngIf="savingMerchant">autorenew</span>
              {{ savingMerchant ? 'Saving...' : 'Save Changes' }}
            </button>
            <button class="btn-cancel" (click)="cancelMerchantEdit()">Cancel</button>
          </div>

          <div class="save-success" *ngIf="savedMerchantPlanId === plan._id">
            <span class="material-symbols-outlined">check_circle</span> Saved & live!
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
